import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Check, UserPlus, Clock, AlertTriangle } from "lucide-react";

const STATUS_STYLES = {
  todo:        "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200 border-blue-300 dark:border-blue-800",
  in_progress: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200 border-amber-300 dark:border-amber-800",
  done:        "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200 border-emerald-300 dark:border-emerald-800",
  blocked:     "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-200 border-red-300 dark:border-red-800",
};

/**
 * Mobile-first task card — full-width, minimal (name + avatar + status),
 * with large-tap Complete + Assign buttons. Uses Shadcn Avatar for initials.
 */
export default function MobileTaskCard({ task, assignee, onOpen, onComplete, onAssign }) {
  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = task.due_date && task.due_date.slice(0, 10) < today && task.status !== "done";
  const isUpcoming = !!task.upcoming;
  const initials = (assignee?.name || assignee?.email || "?").trim().split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div
      data-testid={`mobile-task-card-${task.id}`}
      className={`w-full bg-white dark:bg-neutral-950 border-2 ${
        isUpcoming ? "border-dashed border-[#B7791F] bg-[#FFF8E1] dark:bg-neutral-900/40 opacity-80"
        : isOverdue ? "border-[#FF3B30]"
        : "border-black dark:border-neutral-800"
      } p-4 active:scale-[0.99] transition-transform`}
    >
      {isUpcoming && (
        <div className="mb-2 text-[10px] font-mono uppercase tracking-[0.15em] text-[#B7791F] flex items-center gap-1">
          <Clock className="h-3 w-3" />
          appears {task.visible_from ? new Date(task.visible_from).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "later"}
        </div>
      )}
      {/* Top row: title + avatar */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <button
          type="button"
          onClick={onOpen}
          data-testid={`mobile-task-open-${task.id}`}
          className="text-left flex-1 min-w-0"
        >
          <h3 className={`text-base font-bold leading-snug break-words ${task.status === "done" ? "line-through text-neutral-500 dark:text-neutral-500" : "text-black dark:text-white"}`}>
            {isOverdue && <AlertTriangle className="inline h-4 w-4 text-[#FF3B30] mr-1 -mt-0.5" />}
            {task.title}
          </h3>
        </button>
        <Avatar className="h-10 w-10 flex-shrink-0 border-2 border-black dark:border-neutral-700">
          <AvatarFallback
            className="text-white text-xs font-black tracking-wider"
            style={{ background: assignee?.avatar_color || "#0D0D0D" }}
            data-testid={`mobile-task-avatar-${task.id}`}
          >
            {initials || "?"}
          </AvatarFallback>
        </Avatar>
      </div>

      {/* Second row: status + due date */}
      <div className="flex items-center justify-between gap-2 mb-4">
        <span
          data-testid={`mobile-task-status-${task.id}`}
          className={`inline-block text-[10px] font-black uppercase tracking-widest border px-2 py-1 ${STATUS_STYLES[task.status] || STATUS_STYLES.todo}`}
        >
          {task.status.replace("_", " ")}
        </span>
        {task.due_date && (
          <span className={`text-xs font-mono flex items-center gap-1 ${isOverdue ? "text-[#FF3B30] font-bold" : "text-neutral-500 dark:text-neutral-400"}`}>
            <Clock className="h-3 w-3" />
            {task.due_date.slice(0, 10)}
          </span>
        )}
      </div>

      {/* Actions — 44pt tap targets */}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onComplete}
          disabled={task.status === "done"}
          data-testid={`mobile-task-complete-${task.id}`}
          className={`min-h-[44px] font-black uppercase tracking-wider text-xs border-2 ${
            task.status === "done"
              ? "bg-neutral-100 dark:bg-neutral-900 border-neutral-300 dark:border-neutral-700 text-neutral-400 dark:text-neutral-500"
              : "bg-[#0D0D0D] dark:bg-white text-white dark:text-black border-[#0D0D0D] dark:border-white active:bg-[#FF3B30] active:border-[#FF3B30]"
          } flex items-center justify-center gap-1.5`}
        >
          <Check className="h-4 w-4" />
          {task.status === "done" ? "Done" : "Complete"}
        </button>
        <button
          type="button"
          onClick={onAssign}
          data-testid={`mobile-task-assign-${task.id}`}
          className="min-h-[44px] font-black uppercase tracking-wider text-xs border-2 border-[#FF3B30] text-[#FF3B30] dark:text-[#FF6B60] dark:border-[#FF6B60] active:bg-[#FF3B30] active:text-white flex items-center justify-center gap-1.5"
        >
          <UserPlus className="h-4 w-4" />
          Assign
        </button>
      </div>
    </div>
  );
}
